import React, { useCallback, useMemo, useState } from "react";
import PieChartSubCategory from "components/ReportsSalesByCategoryReport/PieChartSubCategory";
import List from "components/common/ListWidget";
import moment from "moment";
import { useBusinessContext } from "BusinessApp";
import { getDateHeader } from "helpers/dataHelper";
import {
  useOrderItemSubcategorySummary,
  useOrderItemSummary,
} from "graphql/useOrderItemSummary";
import { utils, writeFile } from "xlsx";
import { toPng } from "html-to-image";
import PieChartCategory from "components/ReportsSalesByCategoryReport/PieChartCategory";
import { useTranslation } from "react-i18next";
import TopMenu from "components/Reports/TopMenu";
import useSubcategories, {
  useCategories,
} from "../../graphql/useSubcategories";
import DocumentPDF from "./DocumentPDF";
import s from "./styles";
import { printDocumentPDF } from "helpers/utils";

function ReportsCategories() {
  const { t } = useTranslation();
  const { storeId, currencyFormat } = useBusinessContext();
  const [date, setDate] = useState({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  });

  const firstGraph = React.useRef(null);
  const secondGraph = React.useRef(null);

  const { orderItemSubcategorySummary, loading: loading1 } =
    useOrderItemSubcategorySummary({
      storeId,
      start: date.range.start.toDate(),
      end: date.range.end.toDate(),
      // limit: 5,
      sort: "total",
    });

  // TODO: PCP-1860
  const { subcategories, loading: loadingSubCategories } = useSubcategories();
  const { categories, loading: loadingCategories } = useCategories();

  // const orderItemCategorySummary = useMemo(() => {
  //   const subcategoriesMap = mapToEntity(subcategories);
  //   const categoriesMap = mapToEntity(categories);
  //   const categoriesResult = {};
  //   orderItemSubcategorySummary.forEach((summary) => {
  //     if (!summary._id.subcategoryId) return;

  //     if (summary._id?.subcategoryId) {
  //       const subcategory = subcategoriesMap[summary._id.subcategoryId];
  //       if (subcategory && subcategory.categoryId) {
  //         if (!categoriesResult[subcategory.categoryId]) {
  //           categoriesResult[subcategory.categoryId] = {
  //             name:
  //               categoriesMap[subcategory.categoryId]?.name ||
  //               subcategory.categoryId,
  //             total: 0,
  //             quantity: 0,
  //             count: 0,
  //           };
  //         }
  //         categoriesResult[subcategory.categoryId].total =
  //           categoriesResult[subcategory.categoryId].total +
  //           Number(summary.total);
  //         categoriesResult[subcategory.categoryId].quantity =
  //           categoriesResult[subcategory.categoryId].quantity +
  //           Number(summary.quantity);
  //         categoriesResult[subcategory.categoryId].count =
  //           categoriesResult[subcategory.categoryId].total +
  //           Number(summary.count);
  //       }
  //     }
  //   });
  //   return Object.keys(categoriesResult).map((key) => categoriesResult[key]);
  // }, [orderItemSubcategorySummary, categories, subcategories]);

  const { orderItemSummary, loading: loading4 } = useOrderItemSummary({
    storeId,
    start: date.range.start.toDate(),
    end: date.range.end.toDate(),
    limit: 100,
    sort: "quantity",
  });

  const { orderItemSummary: creOrderItemSummary, loading: loading6 } =
    useOrderItemSummary({
      storeId,
      start: date.range.start.toDate(),
      end: date.range.end.toDate(),
      type: "cre",
      limit: 100,
      sort: "quantity",
    });

  const [sorting, setSorting] = useState({ columnId: "earnings", order: 1 });

  const summarySales = useMemo(() => {
    const combinedItems = [...orderItemSummary, ...creOrderItemSummary];

    const groupedItems = {};

    combinedItems.forEach((item) => {
      const key = item._id.subcategoryId;
      if (!groupedItems[key]) {
        groupedItems[key] = { ...item, sales: 0, returns: 0, earnings: 0 };

        if (groupedItems.quantity > item.quantity) {
          groupedItems[key]._id = item._id;
        }
      }
      if (item.total < 0) {
        groupedItems[key].returns += item.total;
      } else if (item.total > 0) {
        groupedItems[key].sales += item.total;
      }

      groupedItems[key].earnings = groupedItems[key].earnings + item.total;
    });

    const sortedItems = Object.values(groupedItems).sort(
      (a, b) => b.earnings - a.earnings
    );

    const formattedValues = sortedItems.map((item) => {
      return {
        subcategoryName: item._id.subcategoryName ||
          t("containers.ReportsCategories.unSubCategorized") || (
            <p style={{ fontStyle: "italic" }}>-</p>
          ),
        itemName: item._id.itemName || <p style={{ fontStyle: "italic" }}>-</p>,
        returns: item.returns,
        sales: item.sales,
        earnings: item.earnings,
      };
    });

    return formattedValues;
  }, [orderItemSummary, creOrderItemSummary]);

  const tableData = useMemo(() => {
    return summarySales.map((item) => {
      return [
        item.subcategoryName,
        item.itemName,
        currencyFormat(item.returns),
        currencyFormat(item.sales),
        currencyFormat(item.earnings),
      ];
    });
  }, [summarySales]);

  const subCategoriesChart = useMemo(() => {
    const combinedItems = [...orderItemSummary, ...creOrderItemSummary];

    const groupedItems = {};

    combinedItems.forEach((item) => {
      const key = item._id.subcategoryId;
      if (!groupedItems[key]) {
        groupedItems[key] = { ...item, sales: 0, returns: 0, earnings: 0 };

        if (groupedItems.quantity > item.quantity) {
          groupedItems[key]._id = item._id;
        }
      }
      if (item.total < 0) {
        groupedItems[key].returns += item.total;
      } else if (item.total > 0) {
        groupedItems[key].sales += item.total;
      }

      groupedItems[key].earnings = groupedItems[key].earnings + item.total;
    });

    const sortedItems = Object.values(groupedItems).sort(
      (a, b) => b.sales - a.sales
    );

    const sortedCategories = sortedItems.slice(0, 3);

    const restTotal = sortedItems
      .slice(3)
      .reduce((total, value) => total + value.sales, 0);

    const otherValue =
      (restTotal > 0 && {
        total: restTotal,
        name: t("containers.ReportsCategories.others"),
      }) ||
      null;

    return otherValue ? [...sortedCategories, otherValue] : sortedCategories;
  }, [creOrderItemSummary, orderItemSummary]);

  const categoriesChart = useMemo(() => {
    const categoryGroups = {};
    const uncategorizedId = "uncategorized";
    const uncategorizedName = t("containers.ReportsCategories.uncategorized");

    orderItemSummary
      .filter((v) => v._id.subcategoryId !== null)
      .forEach((v) => {
        const categoryId = subcategories.find(
          (s) => s._id === v._id.subcategoryId
        )?.categoryId;

        const data = {};
        creOrderItemSummary.forEach((v2) => {
          if (v._id.itemId === v2._id.itemId) {
            data.total = v2.total;
          }
        });

        const category = categories.find((c) => c._id === categoryId);
        const total = data.total
          ? parseFloat(v.total - Math.abs(data.total))
          : parseFloat(v.total);

        const effectiveCategoryId = category ? categoryId : uncategorizedId;
        const effectiveCategoryName = category
          ? category.name
          : uncategorizedName;

        if (!categoryGroups[effectiveCategoryId]) {
          categoryGroups[effectiveCategoryId] = {
            total,
            name: effectiveCategoryName,
          };
        } else {
          categoryGroups[effectiveCategoryId].total += total;
        }
      });

    const sortedItems = Object.values(categoryGroups).sort(
      (a, b) => b.total - a.total
    );

    const sortedCategories = sortedItems
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);

    const restTotal = sortedItems
      .slice(3)
      .reduce((total, value) => total + value.total, 0);

    const otherValue =
      restTotal > 0
        ? {
            total: restTotal,
            name: t("containers.ReportsCategories.others"),
          }
        : null;

    return otherValue ? [...sortedCategories, otherValue] : sortedCategories;
  }, [creOrderItemSummary, orderItemSummary, categories, subcategories]);

  const loading =
    loading1 ||
    loadingSubCategories ||
    loadingCategories ||
    loading4 ||
    loading6;

  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        `${t(
          "containers.ReportsCategories.reportsCategoriesFor"
        )} ${getDateHeader(date.range.start, date.range.end)}`,
      ],
      [t("containers.ReportsCategories.categoriesMostSold")],
      ...categoriesChart.map((item) => [item.name, parseFloat(item.total)]),
      [],
      [t("containers.ReportsCategories.subcategoriesMostSold")],
      ...subCategoriesChart.map((item) => [
        item?._id?.subcategoryName || item?.name,
        parseFloat(item?.sales || item?.total),
      ]),
      [],
      [t("containers.ReportsCategories.salesSummary")],
      [
        t("containers.ReportsCategories.subcategory"),
        t("containers.ReportsCategories.bestSellingItem"),
        t("containers.ReportsCategories.returnsBYCategory"),
        t("containers.ReportsCategories.sales"),
        t("containers.ReportsCategories.total"),
      ],
    ];

    summarySales.forEach((item) => {
      worksheetData.push([
        item.subcategoryName,
        item.itemName,
        item.returns,
        item.sales,
        item.earnings,
      ]);
    });

    const workbook = utils.book_new();
    const sheetName = t("containers.ReportsCategories.reportsCategories");
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(
      workbook,
      `${t("containers.ReportsCategories.reportsCategories")}.xlsx`
    );
  }, [
    date.range.end,
    date.range.start,
    categoriesChart,
    subCategoriesChart,
    summarySales,
  ]);

  const getImageGraph = useCallback(async () => {
    if (firstGraph.current === null || secondGraph.current === null) {
      return;
    }

    const img1 = await toPng(firstGraph.current, { cacheBust: true });

    const img2 = await toPng(secondGraph.current, { cacheBust: true });

    await printDocumentPDF(
      <DocumentPDF
        data={{
          dateHeader: getDateHeader(date.range.start, date.range.end)[0],
          tableData,
          img1,
          img2,
        }}
      />,
      `${t("containers.ReportsCategories.reportsCategories")}.pdf`
    );
  }, [date.range.end, date.range.start, tableData]);

  return (
    <s.PageContainer>
      <TopMenu
        loading={loading}
        value={date.value}
        onChange={(value, periodType, range) => {
          setDate({ value, periodType, range });
        }}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        disabled={
          orderItemSummary.length === 0 &&
          orderItemSubcategorySummary.length === 0
        }
      />
      <s.Container>
        <s.ChartComponentsWrapper>
          <PieChartCategory
            orderItemCategorySummary={categoriesChart}
            loadingCategories={loading}
            firstGraph={firstGraph}
          />
          <PieChartSubCategory
            SubcategorySummary={subCategoriesChart}
            loadingSubCategories={loading}
            secondGraph={secondGraph}
          />
        </s.ChartComponentsWrapper>
      </s.Container>

      <s.Container>
        <List
          width="700px"
          items={tableData}
          title={t("containers.ReportsCategories.salesSummary")}
          columns={[
            {
              title: t("containers.ReportsCategories.subcategory"),
            },
            {
              title: t("containers.ReportsCategories.bestSellingItem"),
            },
            {
              title: t("containers.ReportsCategories.returnsBYCategory"),
            },
            {
              title: t("containers.ReportsCategories.sales"),
            },
            {
              title: t("containers.ReportsCategories.total"),
            },
          ]}
          tableStyle={{ gridTemplateColumns: "20fr 21fr 21fr 10fr 10fr " }}
          sorting={sorting}
          changeSorting={(columnId, order) => setSorting({ columnId, order })}
          loading={loading}
        />
      </s.Container>
    </s.PageContainer>
  );
}

export default ReportsCategories;
